import * as Enumerable from 'linq';
import { AdditionalFile, ComplianceAnswerMetadata, ComplianceQuestion, ComplianceQuestionType, Entity, WorkComplianceAnswer, WorkComplianceCode, WorkComplianceItem } from '../entities';
import { EmbeddedCode } from '../entities/shared';
import { ComplianceAnswerType, ComplianceQuestionUnique } from '../requests';

export class WorkComplianceUtility {

    public static isAnswered(complianceQnA: WorkComplianceAnswer): boolean {

        const { beforePhotos, afterPhotos, duringPhotos, attachments, commentAnswered, signatureAnswered, forms, files, confirmation } = complianceQnA;

        const answerd = !!beforePhotos?.length ||
            !!afterPhotos?.length ||
            !!duringPhotos?.length ||
            !!attachments?.length ||
            !!commentAnswered ||
            !!signatureAnswered ||
            !!files?.length ||
            !!forms?.length ||
            confirmation;

        return answerd;
    }

    public static restrictedGenerateComplianceFields(fieldName: string): boolean {
        const allowedFields = [
            'priority',
            'type',
        ];

        return allowedFields.includes(fieldName);
    }

    public static restrictedFieldEmpty(fieldName: ComplianceAnswerType): boolean {
        const fields = [
            ComplianceAnswerType.Confirmation,
            ComplianceAnswerType.Signature,
            ComplianceAnswerType.Comment
        ];

        return fields.includes(fieldName);
    }


    public static isAttachmentField(fieldName: string): boolean {

        const attachmentFields = [
            'attachments',
            'beforePhotos',
            'duringPhotos',
            'afterPhotos',
            'forms',
            'files'
        ];

        return attachmentFields.includes(fieldName);

    }

    public static convertAnswerToWorkItem(answer: WorkComplianceAnswer): WorkComplianceItem {
        return {
            _id: answer._id,
            code: answer?.code,
            codeId: answer?.codeId,
            workOrderId: answer?.workOrderId,
            name: answer?.name,
            completed: answer?.completed ?? false,
            answered: answer?.answered ?? false,
            questionId: answer.questionId,
            type: answer.type
        } as WorkComplianceItem;
    }

    public static updateCodeToWorkComplianceCode(complianceItem: WorkComplianceItem, code: EmbeddedCode): WorkComplianceItem {
        const complianceCode = {
            _id: code._id,
            name: code.name,
            quantity: code.quantity,
            location: code.location,
            description: code.description,
            note: code.note,
            status: code.status,
            codeUoM: code.codeUoM
        } as WorkComplianceCode;

        const updatedWorkItem = { ...complianceItem, code: complianceCode, supervisorCheck: code.supervisorCheck, };
        return updatedWorkItem;
    }

    public static convertCodeToWorkItem(code: EmbeddedCode, workOrderId: string): WorkComplianceItem {
        const complianceCode = {
            _id: code._id,
            name: code.name,
            quantity: code.quantity,
            location: code.location,
            description: code.description,
            note: code.note,
            status: code.status,
            codeUoM: code.codeUoM
        } as WorkComplianceCode;

        return {
            _id: this.getKey(workOrderId, ComplianceQuestionUnique, code._id),
            code: complianceCode,
            codeId: code._id,
            workOrderId: workOrderId,
            name: code?.name,
            completed: code.completed ?? false,
            answered: code.completed ?? false,
            questionId: ComplianceQuestionUnique,
            type: ComplianceQuestionType.Requirement,
            supervisorCheck: code.supervisorCheck,
            mandatory: false,
            hasMedia: false
        } as WorkComplianceItem;
    }

    public static checkAllCodeAnswersAreAnswered(answers: any[], additionalFile: AdditionalFile) {

        const isAnswered = Enumerable.from(answers).any(x => x.answered);

        return isAnswered || !!additionalFile?.attachments?.length;
    }

    public static mapCodeCompletedToCompliance(codes: EmbeddedCode[], workItems: WorkComplianceItem[]): WorkComplianceItem[] {

        const codeMap = Enumerable.from(codes).groupBy(x => x._id).toDictionary(x => x.key(), x => x.first());

        const mapped: WorkComplianceItem[] = [];
        Enumerable
            .from(workItems)
            .forEach(item => {

                if (!item.codeId) {
                    mapped.push(item);
                    return;
                }

                const code = codeMap.get(item.codeId);
                if (!code) return;

                item.completed = code?.completed;
                item.answered = code?.answered;
                item.code.description = code?.description;
                item.code.quantity = code?.quantity;
                item.code.location = code?.location;
                item.code.note = code?.note;
                item.code.supervisorCheck = code?.supervisorCheck;
                item.mandatory = item?.mandatory ?? false;
                item.hasMedia = item?.hasMedia ?? false;

                mapped.push(item);
            });

        return mapped;
    }

    public static getKey(workOrderId: string, questionId: string, codeId: string = null): string {

        const parts: string[] = [
            workOrderId,
            questionId
        ];

        if (codeId)
            parts.push(codeId);

        return parts.join('::');

    }

    public static getAnswer(meta: ComplianceAnswerMetadata, question: ComplianceQuestion, code: Entity = null): WorkComplianceAnswer {

        const codeId = code
            ? code._id
            : null;

        const name = question.name
            ? question.name
            : code.name;

        const answer = {
            ...question,
            _id: WorkComplianceUtility.getKey(meta.workOrderId, question._id, code ? code._id : null),
            active: true,
            name: name.trim(),
            workOrderId: meta.workOrderId,
            questionId: question._id,
            codeId: codeId,
            code: code,
            answered: false,
            completed: false,
            confirmationAnswered: false,
            commentAnswered: null,
            signatureAnswered: null,
            mandatoryAnswered: false,
            forms: [],
            beforePhotos: [],
            duringPhotos: [],
            afterPhotos: [],
            attachments: [],
            mandatory: question?.mandatory
        } as WorkComplianceAnswer;

        return answer;
    }
}

